import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaPlus } from 'react-icons/fa6';
import * as adminRolesService from '../../data/services/adminRolesService';
import * as adminResponsibilitiesService from '../../data/services/adminResponsibilitiesService';
import { getAllOrgDomains } from '../../../../shared/utils/organizationDomainUtil';
import ResponsibilityItem from '../components/ResponsibilityItem';
import ResponsibilityForm from '../components/ResponsibilityForm';
import './AdminRoleDetailScreen.css';

const AdminRoleDetailScreen = () => {
  const navigate = useNavigate();
  const { roleId } = useParams();
  const [role, setRole] = useState(null);
  const [responsibilities, setResponsibilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditingRole, setIsEditingRole] = useState(false);
  const [showAddResponsibility, setShowAddResponsibility] = useState(false);
  const [orgDomains, setOrgDomains] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [roleFormData, setRoleFormData] = useState({
    roleName: '',
    description: '',
    orgId: null,
  });

  const loadOrganizations = useCallback(async () => {
    try {
      setLoadingOrgs(true);
      const orgs = await getAllOrgDomains();
      setOrgDomains(orgs);
    } catch (error) {
      console.error('Error loading organizations:', error);
    } finally {
      setLoadingOrgs(false);
    }
  }, []);

  const loadRole = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminRolesService.getRoleById(roleId);
      setRole(data);
      setResponsibilities(data.responsibilities || []);
      setRoleFormData({
        roleName: data.roleName || '',
        description: data.description || '',
        orgId: data.orgId || null,
      });
    } catch (err) {
      console.error('Error loading role:', err);
      setError(err.message || 'Failed to load role');
    } finally {
      setLoading(false);
    }
  }, [roleId]);

  useEffect(() => {
    loadRole();
    loadOrganizations();
  }, [loadRole, loadOrganizations]);

  const handleBack = () => {
    navigate('/admin/roles');
  };

  const handleEditRole = () => {
    setIsEditingRole(true);
  };

  const handleSaveRole = async () => {
    try {
      const updated = await adminRolesService.updateRole(roleId, {
        roleName: roleFormData.roleName.trim(),
        description: roleFormData.description.trim() || null,
        orgId: roleFormData.orgId || null,
      });
      setRole(updated);
      setIsEditingRole(false);
    } catch (error) {
      console.error('Error updating role:', error);
      alert(error.message || 'Failed to update role');
    }
  };

  const handleCancelEditRole = () => {
    setRoleFormData({
      roleName: role.roleName || '',
      description: role.description || '',
      orgId: role.orgId || null,
    });
    setIsEditingRole(false);
  };

  const handleAddResponsibility = async (data) => {
    try {
      const newResp = await adminResponsibilitiesService.createResponsibility({
        ...data,
        roleId: roleId,
      });
      setResponsibilities(prev => [...prev, { ...newResp, frameworks: [], frameworkCount: 0 }]);
      setShowAddResponsibility(false);
    } catch (error) {
      console.error('Error adding responsibility:', error);
      alert(error.message || 'Failed to add responsibility');
    }
  };

  const handleUpdateResponsibility = (updated) => {
    setResponsibilities(prev =>
      prev.map(r => r.responsibilityId === updated.responsibilityId ? { ...updated, frameworks: r.frameworks || [], frameworkCount: r.frameworkCount || 0 } : r)
    );
  };

  const handleDeleteResponsibility = async (responsibilityId) => {
    try {
      await adminResponsibilitiesService.deleteResponsibility(responsibilityId);
      setResponsibilities(prev => prev.filter(r => r.responsibilityId !== responsibilityId));
    } catch (error) {
      console.error('Error deleting responsibility:', error);
      alert(error.message || 'Failed to delete responsibility');
    }
  };

  if (loading) {
    return (
      <div className="admin-role-detail-screen">
        <div className="loading">Loading role...</div>
      </div>
    );
  }

  if (error || !role) {
    return (
      <div className="admin-role-detail-screen">
        <div className="error-message">Error: {error || 'Role not found'}</div>
        <button onClick={handleBack} className="back-button">Back to Roles</button>
      </div>
    );
  }

  return (
    <div className="admin-role-detail-screen">
      <div className="admin-role-detail-header">
        <button
          className="back-button"
          onClick={handleBack}
          aria-label="Back to Roles"
        >
          <FaArrowLeft className="back-icon" />
        </button>
        <div className="role-header-content">
          {isEditingRole ? (
            <div className="role-edit-form">
              <input
                type="text"
                className="role-name-input"
                value={roleFormData.roleName}
                onChange={(e) => setRoleFormData(prev => ({ ...prev, roleName: e.target.value }))}
                placeholder="Role name"
              />
              <textarea
                className="role-description-input"
                value={roleFormData.description}
                onChange={(e) => setRoleFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Role description"
                rows={3}
              />
              <select
                className="role-description-input"
                value={roleFormData.orgId || ''}
                onChange={(e) => setRoleFormData(prev => ({ ...prev, orgId: e.target.value === '' ? null : e.target.value }))}
                disabled={loadingOrgs}
                style={{ cursor: loadingOrgs ? 'wait' : 'pointer', marginTop: '0.5rem' }}
              >
                <option value="">Global (all organizations)</option>
                {orgDomains.map((org) => (
                  <option key={org.orgId} value={org.orgId}>
                    {org.domain}
                  </option>
                ))}
              </select>
              <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.25rem' }}>
                {roleFormData.orgId === null 
                  ? 'Global roles are visible to all organizations.'
                  : `This role is specific to ${orgDomains.find(o => o.orgId === roleFormData.orgId)?.domain || roleFormData.orgId}.`
                }
              </p>
              <div className="role-edit-actions">
                <button className="cancel-button" onClick={handleCancelEditRole}>
                  Cancel
                </button>
                <button className="save-button" onClick={handleSaveRole}>
                  Save
                </button>
              </div>
            </div>
          ) : (
            <>
              <h1 className="role-title">{role.roleName}</h1>
              {role.description && (
                <p className="role-description">{role.description}</p>
              )}
              {role.orgId ? (
                <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
                  Organization: {orgDomains.find(o => o.orgId === role.orgId)?.domain || role.orgId}
                </p>
              ) : (
                <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
                  Organization: Global (all organizations)
                </p>
              )}
              <button className="edit-role-button" onClick={handleEditRole}>
                Edit Role
              </button>
            </>
          )}
        </div>
      </div>

      <div className="responsibilities-section">
        <div className="responsibilities-header">
          <h2 className="section-title">Responsibilities</h2>
          {!showAddResponsibility && (
            <button
              className="add-responsibility-button"
              onClick={() => setShowAddResponsibility(true)}
            >
              <FaPlus className="add-icon" />
              <span>Add Responsibility</span>
            </button>
          )}
        </div>

        {showAddResponsibility && (
          <div className="add-responsibility-form-container">
            <ResponsibilityForm
              roleId={roleId}
              onSave={handleAddResponsibility}
              onCancel={() => setShowAddResponsibility(false)}
            />
          </div>
        )}

        <div className="responsibilities-list">
          {responsibilities.length > 0 ? (
            responsibilities.map((responsibility) => (
              <ResponsibilityItem
                key={responsibility.responsibilityId}
                responsibility={responsibility}
                roleId={roleId}
                onUpdate={handleUpdateResponsibility}
                onDelete={handleDeleteResponsibility}
              />
            ))
          ) : (
            <div className="empty-message">
              No responsibilities yet. Click "Add Responsibility" to create one.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminRoleDetailScreen;
